import React, { Component } from "react";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { Grid } from "semantic-ui-react";
import { Redirect } from "react-router-dom";
import ErrorWrapper from "~/components/wrapper/ErrorWrapper";
import StepsHeader, { Steps } from "../components/StepsHeader";
import ReviewJobStore from "../stores/ReviewJobStore";
import ImagesDetails from "./ReviewJobPage/ImagesDetails";
import JobSummary from "../components/JobSummary";
import JobLabels from "../components/JobLabels";
import PromiseButton from "~/components/element/PromiseButton";
import ContentLoader from "~/components/element/ContentLoader";
import JobBreadcrumb from "../components/JobBreadcrumb";
import PricingInfoMessage from "../components/PricingInfoMessage";
import "../styles/ReviewJob.less";
import FlashMessageStore from "~/stores/FlashMessageStore";

@observer
class ReviewJobPage extends Component {
  reviewJobStore = null;

  constructor(props) {
    super(props);
    const { photoEditingJobId, match } = props;

    this.reviewJobStore = new ReviewJobStore(photoEditingJobId || match.params.photoEditingJobId);
  }

  componentDidMount() {
    this.loadPage();
  }

  loadPage() {
    const { showError } = this.props;

    this.reviewJobStore.load().catch(error => showError(error.message));
  }

  payAndProcess() {
    const { continueCallback, showError, flashMessageStore } = this.props;

    return this.reviewJobStore
      .payAndProcess()
      .then(() => {
        flashMessageStore.setMessage("Your order has been placed. You will receive an email once the job is done.");
        continueCallback && continueCallback();
      })
      .catch(error => {
        // This is not an unexpected error
        if (error.message === "Not enough credits to proceed!") {
          flashMessageStore.setMessage(error.message, "negative");
        } else {
          showError(error.message);
        }
      });
  }

  redirectToHomePage() {
    this.reviewJobStore.redirectTo = "/";
  }

  render() {
    const {
      isSavingImage,
      photoEditingJobId,
      photoEditingJob,
      isLoading,
      reviewImagesUrl,
      label,
      isSavingLabel,
      redirectTo,
      costPerItem,
      totalCost,
    } = this.reviewJobStore;

    const { showError, continueCallback, stepsHeaderClickCallback, editImageCallback, viewJobsCallback } = this.props;

    // only redirect if there's no continueCallback
    if (redirectTo && !continueCallback) {
      return <Redirect to={redirectTo} />;
    }

    if (isLoading) {
      return (
        <Grid>
          <Grid.Row>
            <Grid.Column width={16}>
              <JobBreadcrumb
                photoEditingJobId={photoEditingJobId}
                viewJobsCallback={viewJobsCallback || (() => this.redirectToHomePage())}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={16} style={{ minHeight: "200px" }}>
              <ContentLoader />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      );
    }

    return (
      <Grid>
        <Grid.Row>
          <Grid.Column width={16}>
            <JobBreadcrumb
              photoEditingJobId={photoEditingJobId}
              viewJobsCallback={viewJobsCallback || (() => this.redirectToHomePage())}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row textAlign="center">
          <Grid.Column width={16}>
            <StepsHeader
              photoEditingJobId={photoEditingJobId}
              currentStepIndex={Steps.REVIEW_JOB}
              clickCallback={stepsHeaderClickCallback}
            />
          </Grid.Column>
        </Grid.Row>
        <PricingInfoMessage photoEditingJob={photoEditingJob} />
        <Grid.Row>
          <Grid.Column width={12}>
            <ImagesDetails
              isSavingImage={isSavingImage}
              photoEditingJobTypeId={photoEditingJob.typeId}
              photoEditingImages={photoEditingJob.photoEditingImages}
              reviewImagesUrl={reviewImagesUrl}
              batchUpdateImageSettingsCallback={settings =>
                this.reviewJobStore.batchUpdateImageSettings(settings).catch(error => showError(error.message))
              }
              toggleTimeOfDayCallback={photoEditingImageId =>
                this.reviewJobStore.toggleTimeOfDay(photoEditingImageId).catch(error => showError(error.message))
              }
              toggleLocationCallback={photoEditingImageId =>
                this.reviewJobStore.toggleLocation(photoEditingImageId).catch(error => showError(error.message))
              }
              removeImageCallback={photoEditingImageId =>
                this.reviewJobStore.removeImage(photoEditingImageId).catch(error => showError(error.message))
              }
              editCallback={editImageCallback}
            />
          </Grid.Column>
          <Grid.Column width={4}>
            <JobSummary photoEditingJob={photoEditingJob} costPerItem={costPerItem} totalCost={totalCost} />
            <JobLabels
              label={label}
              disabled={isSavingLabel}
              photoEditingLabels={photoEditingJob.photoEditingLabels}
              deletePhotoEditingLabelCallback={photoEditingLabelId =>
                this.reviewJobStore
                  .deletePhotoEditingLabel(photoEditingLabelId)
                  .catch(error => showError(error.message))
              }
              onLabelChangeCallback={value => this.reviewJobStore.onLabelChange(value)}
              addPhotoEditingLabelCallback={() =>
                this.reviewJobStore.addPhotoEditingLabel().catch(error => showError(error.message))
              }
            />
            <PromiseButton disabled={isSavingImage || isSavingLabel} fluid primary onClick={() => this.payAndProcess()}>
              {totalCost !== 0 ? `Pay & Process` : `Process`}
            </PromiseButton>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }
}

// "match" property provided by react router
ReviewJobPage.propTypes = {
  showError: PropTypes.func.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      photoEditingJobId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }).isRequired,
  }),
  flashMessageStore: PropTypes.instanceOf(FlashMessageStore),
  // These are passed when scoped to a tour and not in a react-router scope
  photoEditingJobId: PropTypes.number,
  continueCallback: PropTypes.func,
  stepsHeaderClickCallback: PropTypes.func,
  editImageCallback: PropTypes.func,
  viewJobsCallback: PropTypes.func,
};

export default ErrorWrapper(ReviewJobPage);
